import { cognitoCredentialsProvider } from 'aws-amplify/auth/cognito';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';

import { getRegionSelector, useRegionStore } from '@packages/stores';
import { TRIP_STATUS } from '@packages/types';
import { wait } from '@packages/utils';

import { Container } from '@components';
import { PolicyDetails } from '@components/policyDetails/policyDetails.component';
import { withScope } from '@sentry/react';
import {
  openModalSelector,
  useFeatureFlagsStore,
  useGlobalStore,
  useGuestStore,
  useModalStore,
  useTripStore,
} from '@store';
import { clearIdentityId, clearSessionStorage, growsurfApi } from '@utils';

import { ConfirmationBanner } from './confirmationBanner/confirmationBanner.component';
import { DownloadFreely } from './downloadFreely/downloadFreely.component';
import { GooglePayNotification } from './googlePayNotification/googlePayNotification.component';
import { LeaveAReview } from './leaveAReview/leaveAReview.component';
import { NeedAssistance } from './needAssistance/needAssistance.component';
import { PolicyConfirmation } from './policyConfirmation/policyConfirmation.component';
import { WhatsNext } from './whatsNext/whatsNext.component';

export const Confirmation = () => {
  const guest = useGuestStore(state => state.guest);
  const getTrip = useTripStore(state => state.getTrip);
  const openModal = useModalStore(openModalSelector);
  const timer = useRef<number>();
  const region = useRegionStore(getRegionSelector);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const tripStatus = useTripStore(state => state.trip.state);
  const isReferralModalClosed = useGlobalStore(state => state.isReferralModalClosed);
  const referralFlag = useFeatureFlagsStore(state => state?.featureFlags?.['referral']);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      window.confirm('Going back will lose your trip confirmation data');
      clearSessionStorage();
      window.location.href = `/quote/${region?.country?.toLowerCase() ?? ''}`;
    }
  }, [blocker.state, region?.country]);

  useEffect(() => {
    withScope(async scope => {
      scope.setTag('source', 'clearCredentialsAndIdentityId');
      await getTrip();
      await cognitoCredentialsProvider.clearCredentialsAndIdentityId();
      clearIdentityId();
    });
  }, [getTrip]);

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    timer.current = window.setTimeout(() => {
      setIsCopied(false);
    }, 2000);

    return () => {
      window.clearTimeout(timer.current);
    };
  }, [isCopied]);

  const isPaid = tripStatus === TRIP_STATUS.PAID;

  const handleReferralModal = useCallback(async () => {
    if (
      referralFlag?.isEnabled &&
      isPaid &&
      !isReferralModalClosed &&
      guest?.email &&
      guest?.firstName &&
      guest?.lastName
    ) {
      await growsurfApi.init();
      await wait(200);
      openModal('Modal', {
        body: {
          type: 'ReferralModal',
          discountPercentage: referralFlag.payload?.discountPercentage,
          earnDollars: referralFlag.payload?.earnDollars,
        },
        shouldCloseOnOverlayClick: false,
        showCloseButton: false,
        size: 'lg',
      });
    }
  }, [
    guest?.email,
    guest?.firstName,
    guest?.lastName,
    isPaid,
    isReferralModalClosed,
    openModal,
    referralFlag?.isEnabled,
    referralFlag?.payload?.discountPercentage,
    referralFlag?.payload?.earnDollars,
  ]);

  useEffect(() => {
    handleReferralModal();
  }, [handleReferralModal]);

  return (
    <>
      <ConfirmationBanner />
      <Container className="grid grid-cols-12 gap-s32">
        <div className="bg-surface-primary col-span-12 lg:col-span-7 p-s16 rounded-br/md gap-s24 flex flex-col">
          <GooglePayNotification />
          <PolicyConfirmation />
          <WhatsNext />
          <DownloadFreely />
          <LeaveAReview />
          <NeedAssistance />
        </div>
        <div className="hidden lg:block lg:col-span-5">
          <PolicyDetails />
        </div>
      </Container>
    </>
  );
};
